import React, { useState, useEffect, useRef, useCallback } from 'react';
import useKioskManifest from '../hooks/useKioskManifest';

const API = process.env.REACT_APP_URL;
const KIOSK_AUTH_KEY = 'kiosk_kit_auth';

/* ── Geo helpers ── */
function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('GPS not supported'));
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      err => reject(err.code === 1
        ? new Error('Location access denied. Tap the lock icon in your address bar, set Location to "Allow", then reload.')
        : new Error('Unable to get location. Enable GPS and try again.')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

function getSavedAuth() {
  try {
    const raw = localStorage.getItem(KIOSK_AUTH_KEY);
    if (!raw) return null;
    const auth = JSON.parse(raw);
    if (auth.expiresAt && Date.now() < auth.expiresAt) return auth;
    localStorage.removeItem(KIOSK_AUTH_KEY);
    return null;
  } catch { return null; }
}

/* ── Dynamic favicon ── */
function setFavicon() {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 64, 64);
  g.addColorStop(0, '#7c3aed'); g.addColorStop(1, '#a78bfa');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(32, 32, 32, 0, Math.PI * 2); ctx.fill();
  // Phone icon
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.roundRect(22, 10, 20, 36, 3); ctx.stroke();
  ctx.beginPath(); ctx.arc(32, 40, 2, 0, Math.PI * 2); ctx.fillStyle = '#fff'; ctx.fill();
  // MYC text
  ctx.font = 'bold 11px Arial'; ctx.textAlign = 'center';
  ctx.fillText('MYC', 32, 60);
  const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
  link.rel = 'icon'; link.href = canvas.toDataURL();
  document.head.appendChild(link);
  document.title = 'MYC — Quick Lead';
}

/* ── Time formatter ── */
function fmtT(d) {
  const h = d.getHours(), m = d.getMinutes();
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return h12 + ':' + String(m).padStart(2, '0') + ' ' + period;
}

const AILMENT_OPTIONS = ['Lower Back Pain', 'Cervical / Neck Pain', 'Knee Pain', 'Shoulder Pain', 'Sciatica', 'Spondylitis', 'Arthritis', 'Frozen Shoulder', 'Slip Disc', 'Other'];
const TEMP_OPTIONS = ['Hot (Serious / Urgent)', 'Warm (Interested)', 'Cold (Just Enquiry)'];
const SOURCE_OPTIONS = ['Walk-in', 'Phone Call', 'WhatsApp', 'Website', 'Social Media', 'Referral', 'Other'];

export default function KioskKit() {
  useKioskManifest('/manifest-kit.json', '#065f46');
  const [kioskAuth, setKioskAuth] = useState(() => getSavedAuth());
  const [authUser, setAuthUser] = useState('');
  const [authOtp, setAuthOtp] = useState('');
  const [authStep, setAuthStep] = useState('username');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authResend, setAuthResend] = useState(0);
  const [authPassword, setAuthPassword] = useState('');
  const authTimerRef = useRef(null);
  const otpRef = useRef(null);
  const pwRef = useRef(null);

  const [geoState, setGeoState] = useState('checking');
  const [geoError, setGeoError] = useState('');
  const [coords, setCoords] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const deferredPrompt = useRef(null);

  const [branch, setBranch] = useState(() => localStorage.getItem('kiosk_kit_branch') || 'MedicalYogaTherapyCentre');
  const [branches, setBranches] = useState([]);
  const [branchData, setBranchData] = useState(null); // full branch object with address/phone
  const [showSettings, setShowSettings] = useState(false);

  const emptyForm = { name: '', number: '', enquiredForName: '', enquiryDetails: '', enquirySource: '', ailments: [], leadTemperature: '', comments: '' };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  // ── Appointment Booking Modal state ──
  const [apptModal, setApptModal] = useState(null);
  const [apptTherapists, setApptTherapists] = useState([]);
  const [apptSelectedTherapist, setApptSelectedTherapist] = useState('');
  const [apptDate, setApptDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); });
  const [apptSlots, setApptSlots] = useState([]);
  const [apptSelectedSlot, setApptSelectedSlot] = useState(null);
  const [apptLoading, setApptLoading] = useState(false);
  const [apptBooking, setApptBooking] = useState(false);
  const [apptError, setApptError] = useState('');
  const [apptBooked, setApptBooked] = useState(null);

  useEffect(() => { setFavicon(); }, []);

  // ── Install prompt (Android) + iOS instructions ──
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); deferredPrompt.current = e; setShowInstall(true); };
    window.addEventListener('beforeinstallprompt', handler);
    if (!localStorage.getItem('kiosk_kit_install_prompted')) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS || !('beforeinstallprompt' in window)) {
        setShowInstall(true);
        localStorage.setItem('kiosk_kit_install_prompted', '1');
      }
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  const handleInstall = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
    }
    setShowInstall(false);
    localStorage.setItem('kiosk_kit_install_prompted', '1');
  };

  useEffect(() => {
    (async () => {
      try { const c = await getGeo(); setCoords(c); setGeoState('ok'); }
      catch (err) { setGeoState(err.message.includes('denied') ? 'denied' : 'error'); setGeoError(err.message); }
    })();
  }, []);

  useEffect(() => {
    fetch(`${API}/patients/public/branches`).then(r => r.json()).then(data => {
      setBranches(data || []);
      if (!branch && data?.length > 0) { const first = data[0].name; setBranch(first); localStorage.setItem('kiosk_kit_branch', first); }
      // Store full branch data for selected branch (address, phone)
      const match = (data || []).find(b => b.name === branch) || (data || [])[0];
      if (match) setBranchData(match);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch therapists when appointment modal opens, filter out on-leave for selected date
  useEffect(() => {
    if (!apptModal || !kioskAuth || !apptDate) return;
    fetch(`${API}/users/staff/all`, {
      headers: { 'Authorization': `Bearer ${kioskAuth.token}`, 'x-username': kioskAuth.username },
    }).then(r => r.json()).then(async (data) => {
      const allTherapists = (data || []).filter(u => u.roles && u.roles.some(r => ['therapist', 'doctor', 'owner'].includes(r)));
      // Check leave status for each therapist on the selected date
      const available = [];
      for (const t of allTherapists) {
        try {
          const sr = await fetch(`${API}/appointments/public/slots/${t.username}?date=${apptDate}`);
          const sd = await sr.json();
          if (!sd.onLeave) available.push(t);
        } catch { available.push(t); } // on error, include them
      }
      setApptTherapists(available);
      // Default to deepak if available, otherwise first therapist
      const deepak = available.find(t => t.username.toLowerCase() === 'deepak');
      if (!apptSelectedTherapist || !available.find(t => t.username === apptSelectedTherapist)) {
        setApptSelectedTherapist(deepak ? deepak.username : (available.length > 0 ? available[0].username : ''));
      }
    }).catch(() => {});
  }, [apptModal, apptDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch slots when therapist or date changes
  useEffect(() => {
    if (!apptModal || !apptSelectedTherapist || !apptDate) return;
    setApptLoading(true); setApptSlots([]); setApptSelectedSlot(null);
    fetch(`${API}/appointments/public/slots/${apptSelectedTherapist}?date=${apptDate}`)
      .then(r => r.json()).then(data => {
        const date = new Date(apptDate + 'T00:00:00+05:30');
        const bookedStarts = (data.slots || []).map(s => new Date(s.start).getTime());
        const allSlots = [];
        for (let h = 7; h < 19; h++) {
          for (let m = 0; m < 60; m += 50) {
            if (h === 18 && m > 0) break;
            const start = new Date(date); start.setHours(h, m, 0, 0);
            const end = new Date(start.getTime() + 50 * 60000);
            const bookedCount = bookedStarts.filter(bs => Math.abs(bs - start.getTime()) < 50 * 60000).length;
            const isPast = start.getTime() < Date.now();
            allSlots.push({ start, end, bookedCount, label: fmtT(start) + ' – ' + fmtT(end), available: !isPast && bookedCount < 4 });
          }
        }
        setApptSlots(allSlots);
        setApptLoading(false);
      }).catch(() => setApptLoading(false));
  }, [apptModal, apptSelectedTherapist, apptDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBookAppointment = async () => {
    if (!apptSelectedSlot || !apptModal) return;
    const therapist = apptSelectedTherapist || 'deepak'; // default to Dr. Deepak
    setApptBooking(true); setApptError('');
    try {
      const res = await fetch(`${API}/appointments/public/book`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientCard: apptModal.callleadCard || apptModal.patientCard || '',
          name: apptModal.name,
          number: parseInt(String(apptModal.number).replace(/\D/g, ''), 10),
          therapist,
          start: apptSelectedSlot.start.toISOString(),
          end: apptSelectedSlot.end.toISOString(),
          branch, bookingType: 'consultation', isFree: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to book');
      setApptBooked(data);
    } catch (err) { setApptError(err.message); }
    finally { setApptBooking(false); }
  };

  // Auth handlers
  function startAuthResend() {
    setAuthResend(60);
    clearInterval(authTimerRef.current);
    authTimerRef.current = setInterval(() => {
      setAuthResend(t => { if (t <= 1) { clearInterval(authTimerRef.current); return 0; } return t - 1; });
    }, 1000);
  }

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!authUser.trim()) return setAuthError('Please enter your username.');
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API}/kiosk-otp/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: authUser.trim(), userLat: coords?.lat, userLon: coords?.lon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');
      if (data.mode === 'password') { setAuthStep('password'); }
      else { setAuthStep('otp'); startAuthResend(); if (data.devOtp) setAuthError(`[DEV] OTP: ${data.devOtp}`); }
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const isPassword = authStep === 'password';
    if (!isPassword && (!authOtp || authOtp.length !== 4)) return setAuthError('Enter the 4-digit OTP.');
    if (isPassword && !authPassword) return setAuthError('Enter your password.');
    setAuthLoading(true); setAuthError('');
    try {
      const body = { username: authUser.trim() };
      if (isPassword) body.password = authPassword; else body.otp = authOtp.trim();
      const res = await fetch(`${API}/kiosk-otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || (isPassword ? 'Invalid password' : 'Invalid OTP'));
      const roles = data.data.roles || [];
      const perms = data.data.permissions || [];
      const isSuper = roles.some(r => ['owner', 'solution'].includes(r));
      if (!isSuper && !perms.includes('leads.add') && !data.data.remoteKitAccess) {
        throw new Error('You do not have permission to add leads remotely.');
      }
      const kioskDays = data.data.kioskSessionDays || 5;
      const authObj = { token: data.data.token, username: data.data.username, expiresAt: Date.now() + kioskDays * 24 * 60 * 60 * 1000 };
      localStorage.setItem(KIOSK_AUTH_KEY, JSON.stringify(authObj));
      setKioskAuth(authObj);
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  // Auto-open appointment modal when appointment action succeeds
  useEffect(() => {
    if (success && success.action === 'appointment' && !apptModal) {
      setApptModal({ name: success.name, number: success.number, callleadCard: success.leadId, patientCard: '' });
    }
  }, [success]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authStep === 'otp' && otpRef.current) otpRef.current.focus();
    if (authStep === 'password' && pwRef.current) pwRef.current.focus();
  }, [authStep]);

  const set = useCallback((field) => (e) => { setForm(f => ({ ...f, [field]: e.target.value })); setError(''); }, []);

  const toggleAilment = useCallback((a) => {
    setForm(f => {
      const has = f.ailments.includes(a);
      return { ...f, ailments: has ? f.ailments.filter(x => x !== a) : [...f.ailments, a] };
    });
  }, []);

  // action: 'save' | 'appointment' | 'kit'
  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Please enter caller name.');
    if (!form.number.trim() || form.number.replace(/\D/g, '').length !== 10) return setError('Please enter a valid 10-digit phone number.');
    if (!form.enquiryDetails.trim()) return setError('Please enter enquiry details.');
    if (!branch) return setError('Branch not configured. Tap the gear icon to set it.');
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API}/callleads/profiles/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${kioskAuth.token}`, 'x-username': kioskAuth.username },
        body: JSON.stringify({
          name: form.name.trim(),
          number: parseInt(form.number.replace(/\D/g, ''), 10),
          enquiredForName: form.enquiredForName.trim() || form.name.trim(),
          branch,
          enquiryDate: new Date().toISOString(),
          enquiryDetails: form.enquiryDetails.trim(),
          enquirySource: form.enquirySource || 'Walk-in',
          status: 'enquiry',
          ailments: form.ailments,
          leadTemperature: form.leadTemperature || 'Warm (Interested)',
          comments: form.comments,
          ...(action === 'kit' ? { startKit: true } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data === 'string' ? data : data.message || data.error || 'Failed to add lead');
      const leadId = data._id || '';
      const phone = parseInt(form.number.replace(/\D/g, ''), 10);
      setSuccess({ name: form.name, number: form.number, action, leadId, phone });
      if (action === 'save') {
        setTimeout(() => { setSuccess(null); setForm(emptyForm); }, 6000);
      }
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  // Styles
  const pageStyle = { minHeight: '100vh', background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 16px 40px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
  const cardStyle = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,.08)', width: '100%', maxWidth: 480, overflow: 'hidden' };
  const headerStyle = { background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', padding: '24px 24px 20px', color: '#fff', textAlign: 'center' };
  const inputStyle = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 15, background: '#fafafa', boxSizing: 'border-box', color: '#1e293b' };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 4, display: 'block' };
  const authInputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 16, background: '#fafafa', boxSizing: 'border-box', color: '#1e293b', textAlign: 'center', letterSpacing: 1 };
  const btnStyle = (loading) => ({
    width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
    background: loading ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed, #a78bfa)',
    color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
  });

  // Auth gate
  if (geoState === 'ok' && !kioskAuth) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, maxWidth: 420 }}>
          <div style={headerStyle}>
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 4, marginBottom: 4 }}>MYC</div>
            <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Staff Authentication</div>
          </div>
          <div style={{ padding: '24px 28px 32px' }}>
            {authStep === 'username' && (
              <form onSubmit={handleRequestOtp}>
                <div style={{ fontSize: 14, color: '#475569', marginBottom: 16, textAlign: 'center', lineHeight: 1.5 }}>Enter your staff username to continue.</div>
                <input type="text" value={authUser} onChange={e => { setAuthUser(e.target.value); setAuthError(''); }} placeholder="Username" style={authInputStyle} autoComplete="off" autoFocus />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#7c3aed' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={btnStyle(authLoading)}>{authLoading ? 'Checking...' : 'Continue'}</button>
              </form>
            )}
            {authStep === 'otp' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthOtp(''); setAuthError(''); }} style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>OTP sent to your registered WhatsApp number.</div>
                <input ref={otpRef} type="number" value={authOtp} onChange={e => { setAuthOtp(e.target.value.slice(0, 4)); setAuthError(''); }} placeholder="4-digit OTP" style={authInputStyle} inputMode="numeric" />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#7c3aed' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10, textAlign: 'center' }}>
                  {authResend > 0 ? `Resend in ${authResend}s` : <button type="button" onClick={handleRequestOtp} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', fontSize: 12, textDecoration: 'underline' }}>Resend OTP</button>}
                </div>
                <button type="submit" disabled={authLoading} style={btnStyle(authLoading)}>{authLoading ? 'Verifying...' : 'Verify & Continue'}</button>
              </form>
            )}
            {authStep === 'password' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthPassword(''); setAuthError(''); }} style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>Enter your password to continue.</div>
                <input ref={pwRef} type="password" value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }} placeholder="Password" style={{ ...authInputStyle, letterSpacing: 3 }} />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={btnStyle(authLoading)}>{authLoading ? 'Verifying...' : 'Login & Continue'}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (geoState === 'checking') {
    return (<div style={{ ...pageStyle, justifyContent: 'center' }}><div style={{ textAlign: 'center', color: '#64748b' }}><div style={{ fontSize: 36, marginBottom: 12 }}>📍</div><div style={{ fontSize: 16, fontWeight: 600 }}>Checking location...</div></div></div>);
  }
  if (geoState === 'denied' || geoState === 'error') {
    return (<div style={{ ...pageStyle, justifyContent: 'center' }}><div style={{ ...cardStyle, maxWidth: 400, padding: 32, textAlign: 'center' }}><div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div><h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>Location Required</h2><p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{geoError || 'Please enable GPS.'}</p><button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '10px 24px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Retry</button></div></div>);
  }

  // Success
  if (success) {
    const actionMsg = success.action === 'appointment' ? 'Lead saved! Now book appointment below.'
      : success.action === 'kit' ? 'Lead saved! KIT campaign started.'
      : 'Lead Added!';

    // Show appointment booking modal
    if (success.action === 'appointment' && apptModal) {
      const todayStr = new Date().toISOString().slice(0, 10);
      return (
        <div style={{ ...pageStyle, justifyContent: 'center' }}>
          <div style={{ ...cardStyle, maxWidth: 480, overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb, #60a5fa)', padding: '20px 24px 16px', color: '#fff', position: 'relative' }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 4px' }}>Book Appointment</h2>
              <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>{apptModal.name} — {apptModal.number}</p>
              <button onClick={() => { setApptModal(null); setApptBooked(null); setSuccess(null); setForm(emptyForm); }}
                style={{ position: 'absolute', top: 12, right: 14, background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 16, color: '#fff', fontWeight: 700 }}>✕</button>
            </div>

            {apptBooked ? (() => {
              const dateObj = new Date(apptDate + 'T00:00:00+05:30');
              const dateStr = dateObj.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
              const slotLabel = apptSelectedSlot?.label || '';
              const phone = String(apptModal.number).replace(/\D/g, '');
              const e164 = phone.startsWith('91') ? phone : '91' + phone;
              const addr = branchData?.address || branch || '';
              const contactPhone = branchData?.phone1 ? String(branchData.phone1) : '';
              const lines = [
                'APPOINTMENT BOOKING CONFIRMED',
                '___________',
                '',
                `Namaste ${apptModal.name} Ji!`,
                '',
                'Your appointment has been scheduled.',
                '',
                `Date & Time: ${dateStr}, ${slotLabel}`,
              ];
              if (addr) lines.push('', 'Address:', addr);
              lines.push('', '\uD83D\uDCCD Google map Location:', 'https://maps.app.goo.gl/K4EbctubZdPPYH6cA');
              lines.push('', '\u2728 Please remember to bring your medical reports, if any.');
              lines.push('', '\uD83C\uDF3C Lift facility is available.');
              if (contactPhone) lines.push('', 'For any assistance pls call', contactPhone);
              const waMsg = lines.join('\n');
              const waUrl = 'https://wa.me/' + e164 + '?text=' + encodeURIComponent(waMsg);
              return (
                <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: '#059669', margin: '0 0 8px' }}>Appointment Booked!</h3>
                  <p style={{ fontSize: 13, color: '#475569', margin: '0 0 20px' }}>{dateStr}, {slotLabel}</p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a href={waUrl} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8, border: 'none', background: '#25D366', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.11.546 4.095 1.504 5.82L0 24l6.335-1.461C8.066 23.477 9.98 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.875 0-3.656-.506-5.21-1.44l-.375-.22-3.885.896.975-3.57-.25-.39A9.687 9.687 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
                      Send on WhatsApp
                    </a>
                    <button onClick={() => { setApptModal(null); setApptBooked(null); setSuccess(null); setForm(emptyForm); setApptSelectedTherapist(''); setApptSelectedSlot(null); }}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add Another Lead</button>
                  </div>
                </div>
              );
            })()
            : (
              <div style={{ padding: '16px 24px 24px' }}>
                {/* Date picker */}
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Date</label>
                  <input type="date" value={apptDate} min={todayStr} onChange={e => setApptDate(e.target.value)} style={inputStyle} />
                </div>

                {/* Therapist select (optional — defaults to Dr. Deepak) */}
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Therapist <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8' }}>(optional — defaults to Dr. Deepak)</span></label>
                  <select value={apptSelectedTherapist} onChange={e => setApptSelectedTherapist(e.target.value)} style={inputStyle}>
                    <option value="">Dr. Deepak (Default)</option>
                    {apptTherapists.map(t => <option key={t.username} value={t.username}>{t.username.charAt(0).toUpperCase() + t.username.slice(1)}</option>)}
                  </select>
                </div>

                {/* Loading */}
                {apptLoading && <div style={{ textAlign: 'center', padding: '16px 0', color: '#64748b', fontSize: 13 }}>Loading slots...</div>}

                {/* No slots available */}
                {!apptLoading && apptSlots.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '16px 0', color: '#ef4444', fontSize: 13, fontWeight: 600 }}>
                    No available slots for this date. Try a different date.
                  </div>
                )}

                {/* Time slots grid */}
                {!apptLoading && apptSlots.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Select Time Slot</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                      {apptSlots.map((slot, i) => {
                        const selected = apptSelectedSlot && apptSelectedSlot.start.getTime() === slot.start.getTime();
                        return (
                          <button key={i} type="button" disabled={!slot.available}
                            onClick={() => setApptSelectedSlot(slot)}
                            style={{
                              padding: '8px 4px', borderRadius: 8, fontSize: 11, cursor: slot.available ? 'pointer' : 'not-allowed',
                              border: `1.5px solid ${selected ? '#2563eb' : slot.available ? '#d1d5db' : '#f1f5f9'}`,
                              background: selected ? '#dbeafe' : slot.available ? '#fff' : '#f8fafc',
                              color: slot.available ? (selected ? '#1d4ed8' : '#374151') : '#cbd5e1',
                              fontWeight: selected ? 700 : 400,
                            }}>
                            {slot.label}
                            {slot.bookedCount > 0 && slot.available && <div style={{ fontSize: 9, color: '#94a3b8' }}>{slot.bookedCount}/4</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {apptError && <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 10 }}>{apptError}</div>}

                <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                  <button type="button" onClick={() => { setApptModal(null); setSuccess(null); setForm(emptyForm); }}
                    style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #d1d5db', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button type="button" disabled={!apptSelectedSlot || apptBooking || apptLoading} onClick={handleBookAppointment}
                    style={{ flex: 2, padding: '11px', borderRadius: 10, border: 'none', background: (!apptSelectedSlot || apptBooking || apptLoading) ? '#94a3b8' : '#2563eb', color: '#fff', fontSize: 14, fontWeight: 700, cursor: (!apptSelectedSlot || apptBooking || apptLoading) ? 'not-allowed' : 'pointer' }}>
                    {apptBooking ? 'Booking...' : 'Book Appointment'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{ ...cardStyle, maxWidth: 420, textAlign: 'center', overflow: 'hidden' }}>
          <div style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', padding: '32px 24px 24px', color: '#fff' }}>
            <div style={{ fontSize: 56, marginBottom: 8, lineHeight: 1 }}>{success.action === 'kit' ? '🚀' : '📞'}</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 6px' }}>{actionMsg}</h2>
            <p style={{ fontSize: 14, opacity: 0.9, margin: 0 }}>{success.name} — {success.number}</p>
          </div>
          <div style={{ padding: '24px 28px 32px' }}>
            {success.action === 'save' && <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>This screen will reset automatically...</p>}
            <button onClick={() => { setSuccess(null); setForm(emptyForm); }} style={{ padding: '10px 24px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add Another Lead</button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div style={pageStyle}>
      {showInstall && (
        <div style={{ width: '100%', maxWidth: 480, background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #065f46, #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(6,95,70,0.3)' }}>
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>KIT</span>
          </div>
          <div style={{ flex: 1, fontSize: 13, color: '#065f46', lineHeight: 1.5 }}>
            <strong>Install App:</strong> Tap <strong>Share → Add to Home Screen</strong> to install as a standalone app.
          </div>
          <button onClick={handleInstall} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#065f46', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {deferredPrompt.current ? 'Install' : 'Got it'}
          </button>
        </div>
      )}
      <div style={cardStyle}>
        <div style={{ ...headerStyle, position: 'relative' }}>
          <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 4, marginBottom: 4 }}>MYC</div>
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Medical Yoga Therapy Centre</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '14px 0 0', opacity: 0.95 }}>New Lead / Enquiry</h1>
          {kioskAuth && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>Staff: {kioskAuth.username}</div>}
          <button onClick={() => setShowSettings(s => !s)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16 }}>⚙️</button>
          {kioskAuth && (
            <button onClick={() => { localStorage.removeItem(KIOSK_AUTH_KEY); setKioskAuth(null); setAuthStep('username'); setAuthUser(''); }}
              style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: '#fff', fontWeight: 600 }}>Logout</button>
          )}
        </div>

        {showSettings && (
          <div style={{ padding: '14px 24px', background: '#f5f3ff', borderBottom: '1px solid #ddd6fe' }}>
            <label style={{ ...labelStyle, color: '#6d28d9' }}>Centre Branch</label>
            <select value={branch} onChange={e => { setBranch(e.target.value); localStorage.setItem('kiosk_kit_branch', e.target.value); const m = branches.find(b => b.name === e.target.value); if (m) setBranchData(m); }} style={{ ...inputStyle, background: '#fff' }}>
              <option value="">Select Branch</option>
              {branches.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
            </select>
          </div>
        )}
        {!branch && !showSettings && (
          <div style={{ padding: '12px 24px', background: '#fef3c7', borderBottom: '1px solid #fde68a', fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>⚠️ Branch not set. Tap ⚙️ above to configure.</div>
        )}

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 14px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Caller Name *</label>
              <input type="text" value={form.name} onChange={set('name')} style={inputStyle} placeholder="Who is calling?" autoComplete="off" autoFocus />
            </div>

            <div>
              <label style={labelStyle}>Phone Number *</label>
              <input type="tel" value={form.number} onChange={e => { setForm(f => ({ ...f, number: e.target.value.replace(/\D/g, '').slice(0, 10) })); setError(''); }} style={inputStyle} placeholder="10-digit" maxLength={10} inputMode="numeric" />
            </div>

            <div>
              <label style={labelStyle}>Patient Name</label>
              <input type="text" value={form.enquiredForName} onChange={set('enquiredForName')} style={inputStyle} placeholder="If different" autoComplete="off" />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Enquiry Details *</label>
              <textarea value={form.enquiryDetails} onChange={set('enquiryDetails')} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="What is the enquiry about?" />
            </div>

            <div>
              <label style={labelStyle}>Source</label>
              <select value={form.enquirySource} onChange={set('enquirySource')} style={inputStyle}>
                <option value="">Select</option>
                {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Temperature</label>
              <select value={form.leadTemperature} onChange={set('leadTemperature')} style={inputStyle}>
                <option value="">Select</option>
                {TEMP_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Ailments</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {AILMENT_OPTIONS.map(a => {
                  const sel = form.ailments.includes(a);
                  return (
                    <button type="button" key={a} onClick={() => toggleAilment(a)} style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                      border: `1.5px solid ${sel ? '#7c3aed' : '#d1d5db'}`,
                      background: sel ? '#f5f3ff' : '#fafafa',
                      color: sel ? '#6d28d9' : '#374151',
                      fontWeight: sel ? 600 : 400,
                    }}>{a}</button>
                  );
                })}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Comments</label>
              <textarea value={form.comments} onChange={set('comments')} style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} placeholder="Any notes..." />
            </div>
          </div>

          {error && <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626' }}>{error}</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <button type="submit" disabled={submitting} style={{ ...btnStyle(submitting), marginTop: 0, boxShadow: '0 2px 8px rgba(124,58,237,.25)' }}>
              {submitting ? 'Saving...' : 'Save Only'}
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" disabled={submitting} onClick={e => handleSubmit(e, 'appointment')} style={{ ...btnStyle(submitting), marginTop: 0, flex: 1, background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #2563eb, #60a5fa)', fontSize: 13, padding: '11px' }}>
                Save + Appointment
              </button>
              <button type="button" disabled={submitting} onClick={e => handleSubmit(e, 'kit')} style={{ ...btnStyle(submitting), marginTop: 0, flex: 1, background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #059669, #34d399)', fontSize: 13, padding: '11px' }}>
                Save + Start KIT
              </button>
            </div>
          </div>
        </form>
      </div>

      <div style={{ marginTop: 20, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>MYC Quick Lead / KIT Kiosk</div>
    </div>
  );
}
